import { useCallback, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { Link, useNavigate, useLocation } from "react-router";

import { resetSession } from "@/redux/auth";
import {
  getSchedulingNotificationAction,
  getSchedulingNotificationCountAction,
  resetNotificationError,
  resetNotificationStatusCode,
  resetNotificationSuccess,
  resetScheduleNotificationList,
  resetScheduleNotificationListCount,
} from "@/redux/notification";
import type { RootState } from "@/redux/rootReducers";
import { resetRequestView } from "@/redux/scheduling";

import { CircularAvatar, InfiniteLoader } from "@/components/commons";

import type {
  GetSchedulingNotificationCountPayloadType,
  GetSchedulingNotificationPayloadType,
} from "@/commonTypes/notificationType";

import { CURRENT_PLATFORM } from "@/constant/notificationConstant";

import { formateDateWithTime, reverseDate } from "@/utils/commonFunctions";

import { NoNotification } from "@/assets/images";

type AppProps = {
  token: null | string;
  success: number | null;
  statusCode: number | null;
  message: string | null;
  isOpen: boolean;
  setIsOpens: React.Dispatch<React.SetStateAction<boolean>>;
  schedulingNotificationList: any;
  dispatchResetSession: () => void;
  dispatchResetError: () => void;
  dispatchResetNotificationStatusCode: () => void;
  dispatchResetNotificationSuccess: () => void;
  dispatchResetRequestView: () => void;
  dispatchResetScheduleNotificationList: () => void;
  dispatchResetScheduleNotificationListCount: () => void;
  dispatchGetSchedulingNotificationList: (
    data: GetSchedulingNotificationPayloadType
  ) => void;
  fetching: boolean;
  schedulingNotificationListCount: number;
  dispatchGetSchedulingNotificationCountAction: (
    data: GetSchedulingNotificationCountPayloadType
  ) => void;
};
const SchedulingNotification = ({
  token,
  success,
  statusCode,
  message,
  isOpen,
  setIsOpens,
  schedulingNotificationList,
  dispatchResetSession,
  dispatchResetError,
  dispatchResetNotificationStatusCode,
  dispatchResetNotificationSuccess,
  dispatchGetSchedulingNotificationList,
  dispatchResetRequestView,
  dispatchResetScheduleNotificationList,
  fetching,
  dispatchResetScheduleNotificationListCount,
  schedulingNotificationListCount,
  dispatchGetSchedulingNotificationCountAction,
}: AppProps) => {
  const [limit, setLimit] = useState<number>(10);
  const [offset, setOfset] = useState<number>(0);
  const [finalNotificationList, setFinalNotificationList] = useState<any>([]);
  const scrollRef = useRef<any>("");
  const redirectHistory = useNavigate();
  const location = useLocation();

  useEffect(() => {
    dispatchResetError();
    dispatchResetNotificationSuccess();
    dispatchResetNotificationStatusCode();
    // dispatchResetRequestView();
    dispatchResetScheduleNotificationList();
    dispatchResetScheduleNotificationListCount();
  }, []);

  useEffect(() => {
    const finalData = {
      token,
      limit,
      offset,
      platform: CURRENT_PLATFORM,
    };
    if (offset <= schedulingNotificationListCount && token) {
      dispatchGetSchedulingNotificationList(finalData);
    } else if (offset === 0) {
      dispatchGetSchedulingNotificationList(finalData);
    }
  }, [offset]);

  useEffect(() => {
    if (success === 1 && statusCode === 200) {
      if (message === "All notifications marked as read successfully!") {
        const finalData = {
          token,
          limit,
          offset,
          platform: "web",
        };
        dispatchGetSchedulingNotificationList(finalData);
        dispatchGetSchedulingNotificationCountAction({ token });
      } else if (message === "All notifications cleared successfully!") {
        const finalData = {
          token,
          limit,
          offset,
          platform: "web",
        };
        dispatchGetSchedulingNotificationList(finalData);
        dispatchGetSchedulingNotificationCountAction({ token });
      }
    }
  }, [success, statusCode]);

  useEffect(() => {
    if (schedulingNotificationList?.length) {
      if (offset === 0 && schedulingNotificationList?.length > 0) {
        setFinalNotificationList(schedulingNotificationList);
      } else if (offset > 0 && schedulingNotificationList?.length > 0) {
        setFinalNotificationList((prev: any) => [
          ...prev,
          ...schedulingNotificationList,
        ]);
      }
    } else if (offset === 0) {
      setFinalNotificationList([]);
    }
  }, [schedulingNotificationList]);

  useEffect(() => {
    const infiniteScrollRef = scrollRef.current;
    if (infiniteScrollRef) {
      infiniteScrollRef.addEventListener("scroll", onScroll);
      return () => infiniteScrollRef.removeEventListener("scroll", onScroll);
    }
  }, [schedulingNotificationList]);

  const onScroll = useCallback(
    (event: any) => {
      const scrollTop = scrollRef.current.scrollTop;
      const scrollHeight = scrollRef.current.scrollHeight;
      const clientHeight = scrollRef.current.clientHeight;
      if (
        offset < schedulingNotificationListCount &&
        Math.ceil((75 / 100) * scrollHeight) <= scrollTop + clientHeight
      ) {
        if (schedulingNotificationList?.length > 0) {
          if (!fetching && offset < schedulingNotificationListCount) {
            setOfset(offset + limit);
          }
        }
      }
    },
    [schedulingNotificationList]
  );

  return (
    <div
      ref={scrollRef}
      className="thinScroll"
      style={{ height: "70vh", overflowY: "auto" }}
    >
      {fetching && offset == 0 ? (
        <div
          style={{
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <InfiniteLoader />
        </div>
      ) : finalNotificationList?.length > 0 ? (
        finalNotificationList?.map((el: any) => {
          const firstName = el?.updated_by_user?.first_name;
          const lastName = el?.updated_by_user?.last_name
            ? el?.updated_by_user?.last_name
            : "";
          const userRedirectActiveTab =
            el?.notification_type === "EstimationRequestRejected"
              ? "jobsToBeEstimate"
              : el?.notification_type === "EstimationRequestApproved"
                ? "estimateApproved"
                : "";
          const managerRedirectActiveTab =
            el?.notification_type === "EstimationRequestRecievedForApproval"
              ? "newRequests"
              : el?.notification_type ===
                  "UpdatedEstimationRequestReceivedForApproval"
                ? "newRequests"
                : "";
          const job =
            el?.notification_content &&
            el?.notification_content?.job_details?.type;

          const handleRedirect = () => {
            // Ensure the link is absolute
            const absolutePath = el?.link.startsWith("/")
              ? el.link
              : `/${el.link}`;

            redirectHistory({
              pathname: absolutePath,
              search: `?notification_id=${el?._id}`,
            });
          };
          return (
            <>
              <div className="d-flex">
                <div className="d-flex">
                  <div>
                    {el?.module === "Attendence" &&
                    (el?.notification_type === "TimerOnReminder" ||
                      el?.notification_type === "TimerOffReminder") ? (
                      ""
                    ) : el?.notification_content?.is_admin_view ? (
                      <CircularAvatar
                        image={""}
                        name={`Support Admin`}
                      />
                    ) : (
                      <CircularAvatar
                        image={el?.updated_by_user?.profile_pic?.file_url}
                        name={`${firstName} ${lastName}`}
                      />
                    )}
                  </div>
                  <div className="ml-2">
                    {el?.module === "PTO" && (
                      <Link
                        to={{
                          pathname: `${el?.link}`,
                          search: `?notification_id=${el?._id}`,
                        }}
                      >
                        <div
                          onClick={() => setIsOpens(!isOpen)}
                          style={{ cursor: "pointer", color: "black" }}
                        >
                          <span>
                            {!el?.schedulingRequest &&
                            (el?.content?.includes("rejected") ||
                              el?.content?.includes("approved"))
                              ? `${el?.content}`
                              : `${firstName} ${lastName} ${el?.content}`}
                          </span>
                        </div>
                      </Link>
                    )}
                    {el?.module === "Attendence" &&
                      (el?.notification_type === "TimerOnReminder" ||
                        el?.notification_type === "TimerOffReminder") && (
                        <div style={{ cursor: "pointer", color: "black" }}>
                          <span>{el?.content}</span>
                        </div>
                      )}
                    {el?.module === "Check-In" && (
                      <Link
                        to={{
                          pathname: `${el?.link}`,
                          search: `?notification_id=${el?._id}&active_tab=${
                            el?.notification_content?.active_tab
                              ? el?.notification_content?.active_tab
                              : "allCheckInList"
                          }`,
                        }}
                      >
                        <div
                          onClick={() => setIsOpens(!isOpen)}
                          style={{ cursor: "pointer", color: "black" }}
                        >
                          <span>{el?.content}</span>
                        </div>
                      </Link>
                    )}
                    {el?.module === "resignation" &&
                      (el?.content?.includes("approved") ? (
                        <Link
                          to={{
                            pathname: `${el?.link}`,
                          }}
                        >
                          <div
                            onClick={() => setIsOpens(!isOpen)}
                            style={{ cursor: "pointer", color: "black" }}
                          >
                            <span>{el?.content}</span>
                          </div>
                        </Link>
                      ) : (
                        <Link
                          to={{
                            pathname: `${el?.link}`,
                            search: `?type=${el?.link?.includes("things-to-do") ? "manager" : "hr"}&userId=${el?.notification_content?.user_id}&resignationId=${
                              el?.notification_content?._id
                            }&notificationId=${el?._id}`,
                          }}
                        >
                          <div
                            onClick={() => setIsOpens(!isOpen)}
                            style={{ cursor: "pointer", color: "black" }}
                          >
                            <span>{el?.content}</span>
                          </div>
                        </Link>
                      ))}

                    {el?.module === "Support" && (
                      <Link
                        to={{
                          pathname: `${el?.link}`,
                          search: `?ticket_tab=${el?.notification_content?.ticket_tab}&ticket_id=${el?.notification_content?.request_id}&notification_id=${el?._id}`,
                        }}
                      >
                        <div
                          onClick={() => setIsOpens(!isOpen)}
                          style={{ cursor: "pointer", color: "black" }}
                        >
                          <span>{el?.content}</span>
                        </div>
                      </Link>
                    )}
                    {el?.module === "BANK_STATEMENT" && (
                      <Link
                        to={{
                          pathname: `${el?.link}/${el?.notification_content?.client_code}`,
                          search: `?client_code=${el?.notification_content?.client_code}&notification_id=${el?._id}&company_name=${el?.notification_content?.client_name}`,
                        }}
                      >
                        <div
                          onClick={() => setIsOpens(!isOpen)}
                          style={{ cursor: "pointer", color: "black" }}
                        >
                          <span>{el?.content}</span>
                        </div>
                      </Link>
                    )}

                    {el?.module === "GSTR2a2b" && (
                      <Link
                        to={{
                          pathname: `${el?.link}/${el?.notification_content?.client_code}`,
                          search: `?client_code=${el?.notification_content?.client_code}&notification_id=${el?._id}&company_name=${el?.notification_content?.client_name}`,
                        }}
                      >
                        <div
                          onClick={() => setIsOpens(!isOpen)}
                          style={{ cursor: "pointer", color: "black" }}
                        >
                          <span>{el?.content}</span>
                        </div>
                      </Link>
                    )}

                    {el?.module === "Attendance-Regulerize" && (
                      <div onClick={() => handleRedirect()}>
                        <div
                          onClick={() => setIsOpens(!isOpen)}
                          style={{ cursor: "pointer", color: "black" }}
                        >
                          <span>{el?.content}</span>
                        </div>
                      </div>
                    )}
                    {el?.module === "Performance Evaluation" && (
                      <Link
                        to={{
                          pathname: `${el?.link}`,
                          search: `?notification_id=${el?._id}`,
                        }}
                      >
                        <div
                          onClick={() => setIsOpens(!isOpen)}
                          style={{ cursor: "pointer", color: "black" }}
                        >
                          <span>{el?.content}</span>
                        </div>
                      </Link>
                    )}
                    {el?.module !== "PTO" &&
                      (el?.notification_type === "EstimationRequestRejected" ||
                        el?.notification_type ===
                          "EstimationRequestApproved") && (
                        <Link
                          to={{
                            pathname: `/scheduling/users`,
                            search: `?request_id=${el?.notification_content?.request_id}&activeTab=${userRedirectActiveTab}&client_code=${el?.notification_content?.job_details?.clientDetails?.code}&name=${el?.notification_content?.job_details?.clientDetails?.name}&job_type=${job}&notification_id=${el?._id}`,
                          }}
                        >
                          <div
                            onClick={() => setIsOpens(!isOpen)}
                            style={{ cursor: "pointer" }}
                          >
                            <span
                              style={{
                                wordBreak: "break-all",
                                color: "#262a44",
                                cursor: "pointer",
                              }}
                            >
                              Your request for{" "}
                              <span
                                style={{ color: "#262a44", fontWeight: "500" }}
                              >
                                {job === "future_job"
                                  ? ""
                                  : el?.notification_content?.job_details
                                      ?.job_id + "_"}
                                {
                                  el?.notification_content?.job_details
                                    ?.job_type
                                }
                                _
                                {el?.notification_content?.job_details
                                  ?.period_ended
                                  ? reverseDate(
                                      el?.notification_content?.job_details?.period_ended?.split(
                                        "T"
                                      )[0]
                                    )
                                  : ""}
                              </span>{" "}
                              has been
                              {el?.notification_type ===
                              "EstimationRequestRejected"
                                ? " rejected by"
                                : el?.notification_type ===
                                    "EstimationRequestApproved"
                                  ? " approved by"
                                  : ""}{" "}
                              <span
                                style={{ color: "black", fontWeight: "500" }}
                              >
                                {firstName} {lastName}
                              </span>
                            </span>
                          </div>
                        </Link>
                      )}
                    {(el?.notification_type ===
                      "EstimationRequestRecievedForApproval" ||
                      el?.notification_type ===
                        "UpdatedEstimationRequestReceivedForApproval") && (
                      <Link
                        to={{
                          pathname: `/scheduling/manager/view-request/${el?.notification_content?.request_id}`,
                          search: `?&activeTab=${managerRedirectActiveTab}&notification_id=${el?._id}`,
                        }}
                      >
                        <div onClick={() => setIsOpens(!isOpen)}>
                          <span
                            style={{
                              wordBreak: "break-all",
                              color: "#262a44",
                              cursor: "pointer",
                            }}
                          >
                            <span style={{ color: "black", fontWeight: "500" }}>
                              {firstName} {lastName}
                            </span>{" "}
                            has sent{" "}
                            {el?.notification_type ===
                            "EstimationRequestRecievedForApproval"
                              ? "estimation"
                              : "re-estimation"}{" "}
                            for{" "}
                            <span
                              style={{ color: "#262a44", fontWeight: "500" }}
                            >
                              {el?.notification_content?.job_details?.type ===
                              "future_job"
                                ? ""
                                : `${el?.notification_content?.job_details?.job_id}_`}
                              {el?.notification_content?.job_details?.job_type}_
                              {el?.notification_content?.job_details
                                ?.period_ended
                                ? reverseDate(
                                    el?.notification_content?.job_details?.period_ended.split(
                                      "T"
                                    )[0]
                                  )
                                : ""}
                            </span>{" "}
                            job.
                          </span>
                        </div>
                      </Link>
                    )}
                    <div>
                      <span style={{ fontSize: "13px", color: "#6f7a90" }}>
                        {el?.created_at
                          ? formateDateWithTime(el?.created_at)
                          : ""}
                      </span>
                    </div>
                  </div>
                </div>
                <div style={{ marginLeft: "10px", marginTop: "9px" }}>
                  {!el?.is_page_seen && (
                    <div
                      style={{
                        width: "8px",
                        height: "8px",
                        backgroundColor: "#dd4433",
                        borderRadius: "50%",
                      }}
                    ></div>
                  )}
                </div>
              </div>
              <hr style={{ marginLeft: "-4.7%", width: "108.7%" }} />
            </>
          );
        })
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            height: "80%",
          }}
        >
          <div>
            <img src={NoNotification} />
          </div>
          <div>
            <div>
              <div
                className="mb-2"
                style={{
                  color: "#262a44",
                  fontSize: "18px",
                  textAlign: "center",
                  fontWeight: "500",
                }}
              >
                No New Notifications
              </div>
              <div
                style={{
                  fontSize: "14px",
                  color: "#7c8db0",
                  textAlign: "center",
                }}
              >
                You're all caught up!
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  token: state.auth.token,
  fetching: state.notification.fetching,
  success: state.notification.success,
  error: state.notification.success,
  statusCode: state.notification.statusCode,
  schedulingNotificationList: state.notification.scheduleNotificationList,
  schedulingNotificationListCount:
    state.notification.scheduleNotificationListCount,
  message: state.notification.message,
});

const mapDispatchToProps = (dispatch: any) => ({
  dispatchResetSession: () => dispatch(resetSession()),
  dispatchResetError: () => dispatch(resetNotificationError()),
  dispatchResetNotificationSuccess: () => dispatch(resetNotificationSuccess()),
  dispatchResetNotificationStatusCode: () =>
    dispatch(resetNotificationStatusCode()),
  dispatchGetSchedulingNotificationList: (
    data: GetSchedulingNotificationPayloadType
  ) => dispatch(getSchedulingNotificationAction(data)),
  dispatchResetRequestView: () => dispatch(resetRequestView()),
  dispatchResetScheduleNotificationList: () =>
    dispatch(resetScheduleNotificationList()),
  dispatchResetScheduleNotificationListCount: () =>
    dispatch(resetScheduleNotificationListCount()),
  dispatchGetSchedulingNotificationCountAction: (
    data: GetSchedulingNotificationCountPayloadType
  ) => dispatch(getSchedulingNotificationCountAction(data)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(SchedulingNotification);
